import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  GRANT_IMPACT_CHANGED,
  type GeneralActivitiesGridResponse,
  GeneralActivityGrantImpact,
  type GeneralActivityPersonImpactQuery,
  type GeneralActivityTeamImpactQuery,
  type SetGeneralActivityPersonRequest,
  type SetGeneralActivityPersonResponse,
  type SetGeneralActivityTeamRequest,
  type SetGeneralActivityTeamResponse,
} from '@time-tracking/shared';

import { ApiError, parseJson } from './client.js';
import { useApiFetch } from './useApiFetch.js';

/**
 * Query keys for General activities (categories assigned by leads).
 *
 * Every key starts with `generalActivitiesKeys.all`, so a mutation that can
 * change who has what — a person or team grant write here, or an admin
 * visibility switch in the Categories pane — invalidates the whole family with
 * `invalidateQueries({ queryKey: generalActivitiesKeys.all })`.
 */
export const generalActivitiesKeys = {
  all: ['general-activities'] as const,
  grid: () => [...generalActivitiesKeys.all, 'grid'] as const,
};

/**
 * The admin Categories pane's key. Its "Assigned by leads" coverage line counts
 * grants, so every grant write here makes it stale.
 */
const ADMIN_CATEGORIES_KEY = ['admin-categories'] as const;

/**
 * `GET /v1/general-activities` — the person × activity grid, in one response.
 *
 * The route is approver-or-above (a contributor gets 403), so callers gate it
 * with `enabled` on the viewer's role rather than letting it fail.
 */
export function useGeneralActivitiesGrid(options?: { enabled?: boolean }) {
  const fetch = useApiFetch();
  return useQuery<GeneralActivitiesGridResponse>({
    queryKey: generalActivitiesKeys.grid(),
    queryFn: () =>
      fetch('/v1/general-activities').then((r) => parseJson<GeneralActivitiesGridResponse>(r)),
    enabled: options?.enabled ?? true,
  });
}

/**
 * The 409 `GRANT_IMPACT_CHANGED`: what the confirm dialog showed is no longer
 * what the write would do. Nothing was written; `preview` is the fresh impact
 * for the dialog to re-render and ask again. (A team on sent without an
 * acknowledgement gets this too when it would override another lead's
 * removals — that 409 is the override confirmation's prompt.)
 */
export class GrantImpactChangedError extends ApiError {
  constructor(
    apiMessage: string | undefined,
    public readonly preview: GeneralActivityGrantImpact,
  ) {
    super(409, apiMessage, apiMessage ?? 'Who this affects has changed.', GRANT_IMPACT_CHANGED);
    this.name = 'GrantImpactChangedError';
  }
}

/**
 * `parseJson`, except a `GRANT_IMPACT_CHANGED` 409 keeps its `preview` — the
 * generic parser reads only `error` and `code`, and a response body can be read
 * once.
 */
async function parseGrantWrite<T>(response: Response): Promise<T> {
  if (response.ok) return response.json() as Promise<T>;
  let body: { error?: unknown; code?: unknown; preview?: unknown } | undefined;
  try {
    body = (await response.json()) as typeof body;
  } catch {
    // Empty or non-JSON body — fall through to the status line.
  }
  const apiMessage = typeof body?.error === 'string' ? body.error : undefined;
  const code = typeof body?.code === 'string' ? body.code : undefined;
  if (response.status === 409 && code === GRANT_IMPACT_CHANGED) {
    // Validated, not cast: the dialog re-renders from `preview`'s lists, so a
    // missing or malformed one would crash it. Without a usable preview this is
    // just a 409 — the generic path below reports it.
    const preview = GeneralActivityGrantImpact.safeParse(body?.preview);
    if (preview.success) throw new GrantImpactChangedError(apiMessage, preview.data);
  }
  throw new ApiError(
    response.status,
    apiMessage,
    apiMessage ?? `API error: ${response.status} ${response.statusText}`,
    code,
  );
}

/**
 * Everything a grant write can make stale: the grid (this viewer's and other
 * leads' cells), and the admin pane's coverage line. Run after every write,
 * failed ones included — a 403 or 409 means the grid the user acted on is out
 * of date, and refetching it is how no client-side guess is left behind.
 */
export function invalidateGrantDerived(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: generalActivitiesKeys.all });
  void queryClient.invalidateQueries({ queryKey: ADMIN_CATEGORIES_KEY });
}

/** `PUT /v1/general-activities/person` — a person's individual choice. */
export function useSetGeneralActivityPerson() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<SetGeneralActivityPersonResponse, Error, SetGeneralActivityPersonRequest>({
    mutationFn: (body) =>
      fetch('/v1/general-activities/person', {
        method: 'PUT',
        body: JSON.stringify(body),
      }).then((r) => parseGrantWrite<SetGeneralActivityPersonResponse>(r)),
    onSettled: () => invalidateGrantDerived(queryClient),
  });
}

/** `PUT /v1/general-activities/team` — a team's "whole team" setting. */
export function useSetGeneralActivityTeam() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<SetGeneralActivityTeamResponse, Error, SetGeneralActivityTeamRequest>({
    mutationFn: (body) =>
      fetch('/v1/general-activities/team', {
        method: 'PUT',
        body: JSON.stringify(body),
      }).then((r) => parseGrantWrite<SetGeneralActivityTeamResponse>(r)),
    onSettled: () => invalidateGrantDerived(queryClient),
  });
}

/**
 * The two impact previews, as plain calls rather than cached queries: a count
 * quoted in a confirmation has to describe the database as it is when the
 * dialog opens, never a cached copy from an earlier open (the same reasoning as
 * `useCategoryAudienceImpact`'s `gcTime: 0`).
 */
export function useGeneralActivityImpact() {
  const fetch = useApiFetch();
  return {
    person: (q: GeneralActivityPersonImpactQuery) =>
      fetch(`/v1/general-activities/person-impact?${new URLSearchParams(q).toString()}`).then((r) =>
        parseJson<GeneralActivityGrantImpact>(r),
      ),
    team: (q: GeneralActivityTeamImpactQuery) =>
      fetch(`/v1/general-activities/team-impact?${new URLSearchParams(q).toString()}`).then((r) =>
        parseJson<GeneralActivityGrantImpact>(r),
      ),
  };
}
